/** One-shot effects: projectiles, hit/heal/interrupt/death rings. Purely cosmetic. */
import * as THREE from 'three';
import type { UnitId } from '@keystone/sim';
import { SCHOOL_COLOR } from '../palette';
import type { RenderFx, RenderUnit } from '../types';
import { layFlat, toThree } from './coords';

interface LiveFx {
  fx: RenderFx;
  mesh: THREE.Mesh;
  mat: THREE.MeshBasicMaterial;
}

const sphere = new THREE.SphereGeometry(0.28, 10, 8);
const ring = new THREE.RingGeometry(0.7, 1, 32);

const FX_COLOR: Record<string, number> = { heal: 0x6cff8a, interrupt: 0x6fd3ff, death: 0x222222, hit: 0xffffff, crit: 0xffe066 };

export class FxLayer {
  readonly group = new THREE.Group();
  private live: LiveFx[] = [];

  constructor(private readonly onImpact: (targetId: UnitId, color: number, untilMs: number) => void) {}

  spawn(fx: RenderFx): void {
    const tint = fx.kind === 'projectile' ? new THREE.Color(SCHOOL_COLOR[fx.school ?? 'arcane']) : new THREE.Color(FX_COLOR[fx.kind] ?? 0xffffff);
    const mat = new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide });
    const mesh = new THREE.Mesh(fx.kind === 'projectile' ? sphere : ring, mat);
    if (fx.kind !== 'projectile') layFlat(mesh);
    mesh.visible = false;
    this.group.add(mesh);
    this.live.push({ fx, mesh, mat });
    if (fx.targetId && (fx.kind === 'hit' || fx.kind === 'crit' || fx.kind === 'heal')) {
      this.onImpact(fx.targetId, tint.getHex(), fx.startMs + (fx.kind === 'heal' ? 250 : 120));
    }
  }

  update(timeMs: number, units: ReadonlyMap<UnitId, RenderUnit>, heightOf: (id: UnitId) => number): void {
    this.live = this.live.filter((l) => {
      const t = (timeMs - l.fx.startMs) / l.fx.durationMs;
      const target = l.fx.targetId ? units.get(l.fx.targetId) : undefined;
      if (t >= 1 || !target) {
        this.group.remove(l.mesh);
        l.mat.dispose();
        return false;
      }
      if (t < 0) return true;
      l.mesh.visible = true;
      if (l.fx.kind === 'projectile') {
        const src = l.fx.sourceId ? units.get(l.fx.sourceId) : undefined;
        const from = src?.pos ?? target.pos;
        const h = heightOf(target.id) * 0.6;
        toThree({ x: from.x + (target.pos.x - from.x) * t, y: from.y + (target.pos.y - from.y) * t }, h + Math.sin(t * Math.PI) * 0.8, l.mesh.position);
      } else {
        const base = Math.max(0.9, target.radius);
        const grow = l.fx.kind === 'crit' ? 2.2 : l.fx.kind === 'death' ? 2.5 : 1.5;
        const s = base * (0.6 + grow * t);
        l.mesh.scale.set(s, s, 1);
        const lift = l.fx.kind === 'heal' ? t * heightOf(target.id) : 0.06;
        toThree(target.pos, lift, l.mesh.position);
        l.mat.opacity = 0.85 * (1 - t);
      }
      return true;
    });
  }

  clear(): void {
    for (const l of this.live) {
      this.group.remove(l.mesh);
      l.mat.dispose();
    }
    this.live = [];
  }
}
